import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp,
  Eye, ArrowLeft, Radio, Calendar, Clock, Video, Ticket, FolderOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors"
      >
        <h3 className="text-lg font-bold text-white">{title}</h3>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-zinc-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-zinc-800 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Input Field Component
const InputField = ({ label, value, onChange, placeholder, type = 'text', textarea = false }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-zinc-400">{label}</label>
    {textarea ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none resize-none"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
      />
    )}
  </div>
);

// Image Field with Media Library Picker
const ImageField = ({ label, value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-zinc-400">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or select from library"
          className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="px-4 py-3 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 rounded-lg flex items-center gap-2 transition-colors"
        >
          <FolderOpen className="w-4 h-4" />
          <span className="text-sm font-medium">Browse</span>
        </button>
      </div>
      {value && (
        <div className="mt-2 relative w-32 h-20 rounded-lg overflow-hidden bg-zinc-800">
          <img loading="lazy" decoding="async" 
            src={value.startsWith('http') ? value : `${API_URL}${value}`} 
            alt="" 
            className="w-full h-full object-cover" 
          />
        </div>
      )}
      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(img) => {
          onChange(img.url);
          setShowPicker(false);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

export default function AdminPPVEditor() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ppv/settings`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching PPV settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ppv/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm('Reset to default settings? This cannot be undone.')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ppv/settings/reset`, { method: 'POST' });
      const data = await response.json();
      setSettings(data.settings);
      setMessage({ type: 'success', text: 'Settings reset to defaults' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reset settings' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const addEvent = () => {
    const newEvent = {
      title: 'New Event',
      subtitle: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      time: '7:00 PM CST',
      duration: '2 hours',
      image: '',
      twitchChannel: settings.twitchChannel || '',
      isLive: false,
      eventType: 'performance',
      highlights: [],
      tiers: [
        { id: 'single', name: 'Single Event', price: 9.99, features: ['Live stream access', 'Standard quality video', 'Live chat participation'], popular: false, color: 'zinc' },
        { id: 'replay', name: 'Event + Replay', price: 14.99, features: ['Live stream access', 'HD quality video', '48-hour replay access', 'Live chat participation', 'Downloadable setlist'], popular: true, color: 'yellow' },
        { id: 'vip', name: 'VIP Experience', price: 29.99, features: ['Live stream access', '4K quality video', 'Unlimited replay access', 'VIP chat with Mestre Curtis', 'Post-show Q&A session', 'Exclusive behind-the-scenes', 'Digital event poster'], popular: false, color: 'purple' }
      ]
    };
    setSettings(prev => ({
      ...prev,
      upcomingEvents: [...(prev.upcomingEvents || []), newEvent]
    }));
  };

  const updateEvent = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      upcomingEvents: prev.upcomingEvents.map((event, i) => 
        i === index ? { ...event, [field]: value } : event
      )
    }));
  };

  const removeEvent = (index) => {
    if (!window.confirm('Delete this event?')) return;
    setSettings(prev => ({
      ...prev,
      upcomingEvents: prev.upcomingEvents.filter((_, i) => i !== index)
    }));
  };

  const updateTier = (eventIndex, tierIndex, field, value) => {
    setSettings(prev => ({
      ...prev,
      upcomingEvents: prev.upcomingEvents.map((event, i) => 
        i === eventIndex ? {
          ...event,
          tiers: event.tiers.map((tier, j) => 
            j === tierIndex ? { ...tier, [field]: value } : tier
          )
        } : event
      )
    }));
  };

  const addHighlight = (eventIndex) => {
    setSettings(prev => ({
      ...prev,
      upcomingEvents: prev.upcomingEvents.map((event, i) => 
        i === eventIndex ? {
          ...event,
          highlights: [...(event.highlights || []), { title: 'New Highlight', description: '', icon: 'Sparkles' }]
        } : event
      )
    }));
  };

  const updateHighlight = (eventIndex, highlightIndex, field, value) => {
    setSettings(prev => ({
      ...prev,
      upcomingEvents: prev.upcomingEvents.map((event, i) => 
        i === eventIndex ? {
          ...event,
          highlights: event.highlights.map((h, j) => 
            j === highlightIndex ? { ...h, [field]: value } : h
          )
        } : event
      )
    }));
  };

  const removeHighlight = (eventIndex, highlightIndex) => {
    setSettings(prev => ({
      ...prev,
      upcomingEvents: prev.upcomingEvents.map((event, i) => 
        i === eventIndex ? {
          ...event,
          highlights: event.highlights.filter((_, j) => j !== highlightIndex)
        } : event
      )
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Radio className="w-5 h-5 text-red-500" />
                PPV Events Editor
              </h1>
              <p className="text-sm text-zinc-400">Manage Pay-Per-View events and pricing</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {message && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}
              >
                {message.text}
              </motion.div>
            )}
            
            <Link
              to="/ppv"
              target="_blank"
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </Link>
            
            <button
              onClick={resetSettings}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        
        {/* Hero Settings */}
        <CollapsibleSection title="Page Settings" defaultOpen={true}>
          <div className="grid md:grid-cols-2 gap-4">
            <InputField
              label="Hero Title"
              value={settings.heroTitle}
              onChange={(v) => updateField('heroTitle', v)}
            />
            <InputField
              label="Hero Subtitle"
              value={settings.heroSubtitle}
              onChange={(v) => updateField('heroSubtitle', v)}
            />
            <InputField
              label="Twitch Channel (default for events)"
              value={settings.twitchChannel}
              onChange={(v) => updateField('twitchChannel', v)}
              placeholder="e.g., casasamba"
            />
            <ImageField
              label="Hero Background Image URL"
              value={settings.heroImage}
              onChange={(v) => updateField('heroImage', v)}
            />
          </div>
          <div className="mt-4">
            <InputField
              label="Hero Description"
              value={settings.heroDescription}
              onChange={(v) => updateField('heroDescription', v)}
              textarea
            />
          </div>
        </CollapsibleSection>

        {/* Season Pass */}
        <CollapsibleSection title="Season Pass Settings">
          <div className="grid md:grid-cols-2 gap-4">
            <InputField
              label="Season Pass Price"
              value={settings.seasonPassPrice}
              onChange={(v) => updateField('seasonPassPrice', parseFloat(v) || 0)}
              type="number"
            />
          </div>
          <div className="mt-4 border-t border-zinc-800 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-white">Season Pass Features</h4>
              <button
                onClick={() => updateField('seasonPassFeatures', [...(settings.seasonPassFeatures || []), 'New feature'])}
                className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Feature
              </button>
            </div>
            {settings.seasonPassFeatures?.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => {
                    const newFeatures = [...settings.seasonPassFeatures];
                    newFeatures[index] = e.target.value;
                    updateField('seasonPassFeatures', newFeatures);
                  }}
                  className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                />
                <button
                  onClick={() => updateField('seasonPassFeatures', settings.seasonPassFeatures.filter((_, i) => i !== index))}
                  className="p-2 hover:bg-red-500/20 rounded text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* Upcoming Events */}
        <CollapsibleSection title="Upcoming Events" defaultOpen={true}>
          <div className="flex justify-end mb-4">
            <button
              onClick={addEvent}
              className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg flex items-center gap-2 hover:bg-yellow-500/30"
            >
              <Plus className="w-4 h-4" /> Add Event
            </button>
          </div>
          
          {settings.upcomingEvents?.map((event, eventIndex) => (
            <div key={eventIndex} className="bg-zinc-800/50 rounded-xl p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <Ticket className="w-6 h-6 text-yellow-500" />
                  <h4 className="text-lg font-bold text-white">{event.title || 'Untitled Event'}</h4>
                  {event.isLive && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded">LIVE</span>
                  )}
                </div>
                <button
                  onClick={() => removeEvent(eventIndex)}
                  className="p-2 hover:bg-red-500/20 rounded text-red-400"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <InputField
                  label="Event Title"
                  value={event.title}
                  onChange={(v) => updateEvent(eventIndex, 'title', v)}
                />
                <InputField
                  label="Subtitle"
                  value={event.subtitle}
                  onChange={(v) => updateEvent(eventIndex, 'subtitle', v)}
                />
                <InputField
                  label="Date"
                  value={event.date}
                  onChange={(v) => updateEvent(eventIndex, 'date', v)}
                  type="date"
                />
                <InputField
                  label="Time (e.g., 7:00 PM CST)"
                  value={event.time}
                  onChange={(v) => updateEvent(eventIndex, 'time', v)}
                />
                <InputField
                  label="Duration"
                  value={event.duration}
                  onChange={(v) => updateEvent(eventIndex, 'duration', v)}
                />
                <InputField
                  label="Twitch Channel"
                  value={event.twitchChannel}
                  onChange={(v) => updateEvent(eventIndex, 'twitchChannel', v)}
                />
                <ImageField
                  label="Event Image URL"
                  value={event.image}
                  onChange={(v) => updateEvent(eventIndex, 'image', v)}
                />
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={event.isLive || false}
                      onChange={(e) => updateEvent(eventIndex, 'isLive', e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-sm text-zinc-400">Currently Live</span>
                  </label>
                </div>
              </div>

              <InputField
                label="Description"
                value={event.description}
                onChange={(v) => updateEvent(eventIndex, 'description', v)}
                textarea
              />

              {/* Event Highlights */}
              <div className="mt-4 border-t border-zinc-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-zinc-300">Event Highlights (with Read More)</h5>
                  <button
                    onClick={() => addHighlight(eventIndex)}
                    className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                {event.highlights?.map((highlight, hIndex) => {
                  // Support both string (legacy) and object highlights
                  const isObject = typeof highlight === 'object';
                  const title = isObject ? highlight.title : highlight;
                  const description = isObject ? highlight.description : '';
                  
                  return (
                    <div key={hIndex} className="bg-zinc-800/50 rounded-lg p-4 mb-3">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm text-zinc-400">Highlight {hIndex + 1}</span>
                        <button
                          onClick={() => removeHighlight(eventIndex, hIndex)}
                          className="p-1 hover:bg-red-500/20 rounded text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <InputField
                        label="Title"
                        value={title}
                        onChange={(v) => {
                          if (isObject) {
                            updateHighlight(eventIndex, hIndex, 'title', v);
                          } else {
                            // Convert string to object
                            setSettings(prev => ({
                              ...prev,
                              upcomingEvents: prev.upcomingEvents.map((e, i) => 
                                i === eventIndex ? {
                                  ...e,
                                  highlights: e.highlights.map((h, j) => 
                                    j === hIndex ? { title: v, description: '', icon: 'Sparkles' } : h
                                  )
                                } : e
                              )
                            }));
                          }
                        }}
                        placeholder="e.g., Live Samba Performance"
                      />
                      <div className="mt-2">
                        <InputField
                          label="Description (for Read More popup)"
                          value={description}
                          onChange={(v) => updateHighlight(eventIndex, hIndex, 'description', v)}
                          textarea
                          placeholder="Detailed description shown in popup..."
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pricing Tiers */}
              <div className="mt-4 border-t border-zinc-700 pt-4">
                <h5 className="font-medium text-zinc-300 mb-3">Pricing Tiers</h5>
                <div className="grid md:grid-cols-3 gap-4">
                  {event.tiers?.map((tier, tierIndex) => (
                    <div key={tierIndex} className={`p-4 rounded-lg border ${tier.popular ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{tier.name}</span>
                        {tier.popular && <span className="text-xs text-yellow-400">POPULAR</span>}
                      </div>
                      <InputField
                        label="Price"
                        value={tier.price}
                        onChange={(v) => updateTier(eventIndex, tierIndex, 'price', parseFloat(v) || 0)}
                        type="number"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          
          {(!settings.upcomingEvents || settings.upcomingEvents.length === 0) && (
            <div className="text-center py-12 text-zinc-500">
              <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No upcoming events. Click "Add Event" to create one.</p>
            </div>
          )}
        </CollapsibleSection>

        {/* Bottom Save Button */}
        <div className="flex justify-end pt-6">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
